import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { fetchDecisionStats } from '../../api/trades';
import { extractErrorMessage } from '../../api/extractErrorMessage';
import { visualStrategiesApi } from '../../api/visualStrategies';
import { fmtR, getSkipReasonLabel, getConfirmReasonLabel } from './reviewHelpers';
import DWLFLoader from '../../components/DWLFLoader';

/*
 * Decision Quality — the 2x2 of trading judgement.
 *
 * Every signal decision lands in one of four boxes: took it and it worked,
 * took it and it didn't, skipped it and that saved money, skipped it and it
 * cost money. Takes are judged by their ACTUAL outcomes (closed R), skips by
 * the mechanical counterfactual replay. Open takes and still-running skips
 * are shown as open marks, never judged. Per-reason tables answer the real
 * question: which of my reasons carry alpha?
 */

const MONO = '"Space Mono", monospace';
const GROTESK = '"Space Grotesk", sans-serif';
// Scheme tokens: the light up/down keep contrast on white (DWLF-303).
const GREEN = 'var(--dwlf-up)';
const RED = 'var(--dwlf-down)';
const MUTED = 'var(--dwlf-ink-50)';

const rColor = (r) => (r > 0.005 ? GREEN : r < -0.005 ? RED : MUTED);
const pct = (num, den) => (den > 0 ? `${Math.round((num / den) * 100)}%` : '—');

const MatrixTile = ({ title, count, sub, tone }) => (
  <Card sx={{
    height: '100%',
    border: '1px solid',
    borderColor: tone === 'good' ? 'rgba(34, 197, 94, 0.35)' : tone === 'bad' ? 'rgba(239, 68, 68, 0.35)' : 'var(--dwlf-ink-12)',
    backgroundColor: tone === 'good' ? 'rgba(34, 197, 94, 0.05)' : tone === 'bad' ? 'rgba(239, 68, 68, 0.05)' : 'transparent',
  }}>
    <CardContent>
      <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: MUTED }}>
        {title}
      </Typography>
      <Typography sx={{ fontFamily: GROTESK, fontSize: '2.2rem', fontWeight: 700, color: tone === 'good' ? GREEN : tone === 'bad' ? RED : 'var(--dwlf-text)' }}>
        {count}
      </Typography>
      <Typography sx={{ fontFamily: MONO, fontSize: '0.75rem', color: MUTED }}>
        {sub}
      </Typography>
    </CardContent>
  </Card>
);

const SectionTitle = ({ children }) => (
  <Typography sx={{ fontFamily: GROTESK, fontSize: '1.1rem', fontWeight: 600, color: 'var(--dwlf-text)', mb: 1.5, mt: 4 }}>
    {children}
  </Typography>
);

const headCell = { fontFamily: MONO, fontSize: '0.7rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: MUTED, fontWeight: 600 };

const DecisionQualityPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [strategyNames, setStrategyNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetchDecisionStats(),
      // Names are decoration — the page works with raw strategy ids.
      visualStrategiesApi.listStrategies().catch(() => ({ strategies: [] })),
    ])
      .then(([data, strategiesRes]) => {
        if (cancelled) {return;}
        setStats(data);
        const names = {};
        (strategiesRes?.strategies || strategiesRes?.items || []).forEach((s) => {
          if (s?.strategyId) {names[s.strategyId] = s.name;}
        });
        setStrategyNames(names);
      })
      .catch((e) => {
        if (!cancelled) {setError(extractErrorMessage(e, 'Failed to load decision stats'));}
      })
      .finally(() => {
        if (!cancelled) {setLoading(false);}
      });
    return () => { cancelled = true; };
  }, []);

  const strategyRows = useMemo(() => {
    if (!stats) {return [];}
    return Object.entries(stats.byStrategy)
      .map(([id, rec]) => ({ id, rec }))
      .sort((a, b) => (
        (b.rec.confirmed.count + b.rec.skipped.count) - (a.rec.confirmed.count + a.rec.skipped.count)
      ));
  }, [stats]);

  const confirmReasonRows = useMemo(() => (
    stats ? Object.entries(stats.byConfirmReason).sort((a, b) => b[1].count - a[1].count) : []
  ), [stats]);

  const skipReasonRows = useMemo(() => (
    stats ? Object.entries(stats.bySkipReason).sort((a, b) => b[1].count - a[1].count) : []
  ), [stats]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <DWLFLoader size="large" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">Error loading decision stats: {error}</Alert>
      </Box>
    );
  }

  const { matrix, totals } = stats;
  const judged = matrix.tookRight + matrix.tookWrong + matrix.skipRight + matrix.skipWrong;
  const rightCalls = matrix.tookRight + matrix.skipRight;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={1}>
        <Box>
          <Typography sx={{ fontFamily: GROTESK, fontSize: { xs: '1.75rem', md: '2rem' }, fontWeight: 600, letterSpacing: '-0.03em', color: 'var(--dwlf-text)' }}>
            Decision Quality
          </Typography>
          <Typography sx={{ fontFamily: MONO, fontSize: '0.85rem', color: MUTED }}>
            Every decision judged — takes by their outcomes, skips by the counterfactual
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/trades')}
          sx={{ fontFamily: MONO, fontSize: '0.75rem', letterSpacing: '0.08em', textTransform: 'uppercase', borderRadius: 0, borderColor: 'var(--dwlf-ink-20)', color: 'var(--dwlf-ink-60)' }}
        >
          Trade Journal
        </Button>
      </Box>

      {judged > 0 && (
        <Typography sx={{ fontFamily: MONO, fontSize: '0.85rem', color: MUTED, mb: 3 }}>
          {rightCalls} of {judged} resolved decisions were right ({pct(rightCalls, judged)}) ·
          takes {fmtR(totals.confirmed.netRealizedR)} realized · skips {fmtR(totals.skipped.netSavedR)} saved
        </Typography>
      )}

      {/* The 2x2 */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 6, md: 3 }}>
          <MatrixTile title="Took · Right" count={matrix.tookRight} tone="good"
            sub={`winning takes of ${totals.confirmed.closedCount} closed`} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <MatrixTile title="Took · Wrong" count={matrix.tookWrong} tone="bad"
            sub={`losing takes of ${totals.confirmed.closedCount} closed`} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <MatrixTile title="Skipped · Right" count={matrix.skipRight} tone="good"
            sub={`skips that saved money, of ${totals.skipped.decided} decided`} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <MatrixTile title="Skipped · Wrong" count={matrix.skipWrong} tone="bad"
            sub={`missed winners, of ${totals.skipped.decided} decided`} />
        </Grid>
      </Grid>
      <Box display="flex" gap={1.5} mt={1.5} flexWrap="wrap">
        {matrix.tookOpen > 0 && (
          <Chip size="small" label={`${matrix.tookOpen} open take${matrix.tookOpen !== 1 ? 's' : ''} (${fmtR(totals.confirmed.netOpenR)} mark)`} sx={{ fontFamily: MONO }} />
        )}
        {matrix.skipOpen > 0 && (
          <Chip size="small" label={`${matrix.skipOpen} skip${matrix.skipOpen !== 1 ? 's' : ''} still running`} sx={{ fontFamily: MONO }} />
        )}
        {(matrix.tookFlat + matrix.skipFlat) > 0 && (
          <Chip size="small" label={`${matrix.tookFlat + matrix.skipFlat} flat`} sx={{ fontFamily: MONO }} />
        )}
      </Box>

      {/* Per-strategy */}
      {strategyRows.length > 0 && (
        <>
          <SectionTitle>By strategy</SectionTitle>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headCell}>Strategy</TableCell>
                  <TableCell sx={headCell} align="right">Took</TableCell>
                  <TableCell sx={headCell} align="right">W/L</TableCell>
                  <TableCell sx={headCell} align="right">Realized</TableCell>
                  <TableCell sx={headCell} align="right">Open</TableCell>
                  <TableCell sx={headCell} align="right">Skipped</TableCell>
                  <TableCell sx={headCell} align="right">Saved</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {strategyRows.map(({ id, rec }) => (
                  <TableRow key={id}>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Typography variant="body2" noWrap title={strategyNames[id] || id}>
                        {id === 'manual' ? 'Manual trades' : (strategyNames[id] || id)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{rec.confirmed.count}</TableCell>
                    <TableCell align="right">
                      {rec.confirmed.closedCount > 0 ? `${rec.confirmed.wins}W/${rec.confirmed.losses}L` : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: rColor(rec.confirmed.netRealizedR), fontWeight: 600 }}>
                      {rec.confirmed.closedCount > 0 ? fmtR(rec.confirmed.netRealizedR) : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: rColor(rec.confirmed.netOpenR) }}>
                      {rec.confirmed.openCount > 0 ? `${rec.confirmed.openCount} (${fmtR(rec.confirmed.netOpenR)})` : '—'}
                    </TableCell>
                    <TableCell align="right">{rec.skipped.count}</TableCell>
                    <TableCell align="right" sx={{ color: rColor(rec.skipped.netSavedR), fontWeight: 600 }}>
                      {rec.skipped.count > 0 ? fmtR(rec.skipped.netSavedR) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Reasons for taking */}
      {confirmReasonRows.length > 0 && (
        <>
          <SectionTitle>Why you took trades — and whether those reasons paid</SectionTitle>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headCell}>Reason</TableCell>
                  <TableCell sx={headCell} align="right">Used</TableCell>
                  <TableCell sx={headCell} align="right">Closed</TableCell>
                  <TableCell sx={headCell} align="right">Win rate</TableCell>
                  <TableCell sx={headCell} align="right">Net R</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {confirmReasonRows.map(([reason, r]) => (
                  <TableRow key={reason}>
                    <TableCell>{getConfirmReasonLabel(reason)}</TableCell>
                    <TableCell align="right">{r.count}</TableCell>
                    <TableCell align="right">{r.closedCount || '—'}</TableCell>
                    <TableCell align="right">{pct(r.wins, r.wins + r.losses)}</TableCell>
                    <TableCell align="right" sx={{ color: rColor(r.netRealizedR), fontWeight: 600 }}>
                      {r.closedCount > 0 ? fmtR(r.netRealizedR) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Reasons for skipping */}
      {skipReasonRows.length > 0 && (
        <>
          <SectionTitle>Why you skipped trades — and whether those reasons saved you</SectionTitle>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headCell}>Reason</TableCell>
                  <TableCell sx={headCell} align="right">Used</TableCell>
                  <TableCell sx={headCell} align="right">Decided</TableCell>
                  <TableCell sx={headCell} align="right">Good-skip rate</TableCell>
                  <TableCell sx={headCell} align="right">Net saved</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {skipReasonRows.map(([reason, r]) => (
                  <TableRow key={reason}>
                    <TableCell>{getSkipReasonLabel(reason)}</TableCell>
                    <TableCell align="right">{r.count}</TableCell>
                    <TableCell align="right">{r.decided || '—'}</TableCell>
                    <TableCell align="right">{pct(r.good, r.good + r.bad)}</TableCell>
                    <TableCell align="right" sx={{ color: rColor(r.netSavedR), fontWeight: 600 }}>
                      {fmtR(r.netSavedR)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {judged === 0 && strategyRows.length === 0
        && matrix.tookOpen === 0 && matrix.skipOpen === 0
        && matrix.tookFlat === 0 && matrix.skipFlat === 0 && (
        <Paper sx={{ p: 6, textAlign: 'center', mt: 3 }}>
          <Typography sx={{ fontFamily: GROTESK, fontSize: '1.1rem', color: 'var(--dwlf-ink-70)' }}>
            No decisions to judge yet
          </Typography>
          <Typography sx={{ fontFamily: MONO, fontSize: '0.85rem', color: MUTED, mt: 1 }}>
            Confirm or skip strategy signals with reasons and this page fills itself in.
          </Typography>
        </Paper>
      )}

      <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', color: 'var(--dwlf-ink-35)', mt: 4 }}>
        Takes judged by realized R on closed trades; open positions shown as marks against the latest daily close.
        Skips judged by the mechanical counterfactual replay of the planned levels (see Counterfactual).
        Paper trades excluded. Multi-reason decisions count toward each of their reasons.
      </Typography>
    </Container>
  );
};

export default DecisionQualityPage;
